/**
 * DataList React context.
 *
 * Provides the DataListConfig and UseDataListReturn state to all
 * compound sub-components (Toolbar, Filters, Table, Pagination, etc.).
 *
 * Follows the same context pattern used by EntityForm in the integrated
 * component library: a nullable context with a guard hook that throws
 * when used outside the provider.
 */

import { createContext, useContext } from 'react';
import type { DataListConfig, UseDataListReturn } from '../types';

// ---------------------------------------------------------------------------
// Context Value
// ---------------------------------------------------------------------------

/**
 * Value provided by the DataList root component to all descendants.
 */
export interface DataListContextValue {
  /** The resolved list configuration */
  config: DataListConfig;

  /** List state and actions from useDataList */
  list: UseDataListReturn;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

/**
 * React context for DataList compound components.
 * Initialised as `null` -- the provider is set up in DataListRoot.
 */
export const DataListContext =
  createContext<DataListContextValue | null>(null);

DataListContext.displayName = 'DataListContext';

// ---------------------------------------------------------------------------
// Consumer Hook
// ---------------------------------------------------------------------------

/**
 * Access the DataList context from any compound sub-component.
 *
 * @throws {Error} When called outside of a DataList provider.
 *
 * @example
 * ```tsx
 * function DataListToolbar() {
 *   const { config, list } = useDataListContext();
 *   // ...
 * }
 * ```
 */
export function useDataListContext(): DataListContextValue {
  const context = useContext(DataListContext);
  if (!context) {
    throw new Error(
      'DataList compound components must be used within <DataList>',
    );
  }
  return context;
}
